"use client";

import { useCallback, useMemo } from "react";
import {
  SourceCitationBuilder,
  type SourceField,
  type SourceValues,
} from "@/components/tool-kit/source-citation-builder";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  type BillLabels,
  billIsReady,
  BILL_FIELDS as F,
  formatBill,
} from "@/lib/tool-kit/legal-sources/bill-citation-generator";

/** English strings. A translated content file overrides these through `ui`. */
export const BILL_CITATION_GENERATOR_STRINGS = {
  formHeading: "Bill or resolution details",
  resultsHeading: "Your citations",
  emptyPrompt: "Enter the bill number and the Congress to see your citations.",
  copy: "Copy",
  copied: "Copied!",
  tryExample: "Try an example",
  clear: "Clear",
  titleLabel: "Short title (if it has one)",
  titlePlaceholder: "Mental Health on Campus Improvement Act",
  titleHint:
    "Use the official short title from Congress.gov. Leave it empty for a bill without one.",
  typeLabel: "Type",
  typeHr: "H.R. — House bill",
  typeS: "S. — Senate bill",
  typeHres: "H.Res. — House simple resolution",
  typeSres: "S.Res. — Senate simple resolution",
  typeHjres: "H.J.Res. — House joint resolution",
  typeSjres: "S.J.Res. — Senate joint resolution",
  typeHconres: "H.Con.Res. — House concurrent resolution",
  typeSconres: "S.Con.Res. — Senate concurrent resolution",
  numberLabel: "Number",
  numberPlaceholder: "1100",
  congressLabel: "Congress",
  congressPlaceholder: "113",
  congressHint: "The number only — 113 for the 113th Congress (2013–2014).",
  sessionLabel: "Session (for MLA)",
  sessionNone: "Leave out",
  sessionFirst: "1st session (odd year)",
  sessionSecond: "2nd session (even year)",
  dateLabel: "Date introduced, or of the version you cite",
  datePlaceholder: "YYYY-MM-DD",
  sectionLabel: "Section (optional)",
  sectionPlaceholder: "3",
  websiteLabel: "Website (for MLA)",
  websitePlaceholder: "Congress.gov",
  urlLabel: "URL",
  urlPlaceholder:
    "https://www.congress.gov/bill/113th-congress/house-bill/1100",
  apaLabel: "APA 7th edition",
  mlaLabel: "MLA 9th edition",
  chicagoLabel: "Chicago 18th edition (notes)",
  referenceList: "Reference list",
  parenthetical: "In-text citation (parenthetical)",
  narrative: "In-text citation (narrative)",
  worksCited: "Works Cited",
  inText: "In-text citation",
  footnote: "Footnote",
  apaNote:
    "APA 7 (§11.6) uses the legal form; the year is that of the version you cite. A bill that became law is cited as a statute instead.",
  mlaNote:
    "MLA names the chamber as author and adds the Congress, session, bill number and status at the end (MLA Handbook §5.118).",
  chicagoNote:
    "Chicago cites bills in notes only, in the Bluebook form (CMOS 18 §14.184) — they are not listed in the bibliography.",
  example: {
    title: "Mental Health on Campus Improvement Act",
    type: "hr",
    number: "1100",
    congress: "113",
    session: "1",
    date: "2013-03-12",
    website: "Congress.gov",
    url: "https://www.congress.gov/bill/113th-congress/house-bill/1100",
  },
} as const;

type Strings = typeof BILL_CITATION_GENERATOR_STRINGS;

function buildFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.title,
      kind: "text",
      label: strings.titleLabel,
      placeholder: strings.titlePlaceholder,
      hint: strings.titleHint,
      wide: true,
    },
    {
      id: F.type,
      kind: "select",
      label: strings.typeLabel,
      options: [
        { value: "hr", label: strings.typeHr },
        { value: "s", label: strings.typeS },
        { value: "hres", label: strings.typeHres },
        { value: "sres", label: strings.typeSres },
        { value: "hjres", label: strings.typeHjres },
        { value: "sjres", label: strings.typeSjres },
        { value: "hconres", label: strings.typeHconres },
        { value: "sconres", label: strings.typeSconres },
      ],
    },
    {
      id: F.number,
      kind: "text",
      label: strings.numberLabel,
      placeholder: strings.numberPlaceholder,
    },
    {
      id: F.congress,
      kind: "text",
      label: strings.congressLabel,
      placeholder: strings.congressPlaceholder,
      hint: strings.congressHint,
    },
    {
      id: F.session,
      kind: "select",
      label: strings.sessionLabel,
      options: [
        { value: "", label: strings.sessionNone },
        { value: "1", label: strings.sessionFirst },
        { value: "2", label: strings.sessionSecond },
      ],
    },
    {
      id: F.date,
      kind: "date",
      label: strings.dateLabel,
      placeholder: strings.datePlaceholder,
    },
    {
      id: F.section,
      kind: "text",
      label: strings.sectionLabel,
      placeholder: strings.sectionPlaceholder,
    },
    {
      id: F.website,
      kind: "text",
      label: strings.websiteLabel,
      placeholder: strings.websitePlaceholder,
    },
    {
      id: F.url,
      kind: "url",
      label: strings.urlLabel,
      placeholder: strings.urlPlaceholder,
    },
  ];
}

/** The widget's strings, as the formatter's labels. */
export function billLabels(strings: Strings): BillLabels {
  return {
    apa: strings.apaLabel,
    mla: strings.mlaLabel,
    chicago: strings.chicagoLabel,
    referenceList: strings.referenceList,
    parenthetical: strings.parenthetical,
    narrative: strings.narrative,
    worksCited: strings.worksCited,
    inText: strings.inText,
    footnote: strings.footnote,
    apaNote: strings.apaNote,
    mlaNote: strings.mlaNote,
    chicagoNote: strings.chicagoNote,
  };
}

export default function BillCitationGeneratorWidget() {
  const strings = useToolUi(BILL_CITATION_GENERATOR_STRINGS);
  const fields = useMemo(() => buildFields(strings), [strings]);
  const labels = useMemo(() => billLabels(strings), [strings]);
  const format = useCallback(
    (values: SourceValues) => formatBill(values, labels),
    [labels]
  );

  return (
    <SourceCitationBuilder
      example={strings.example}
      fields={fields}
      format={format}
      isReady={billIsReady}
      strings={strings}
    />
  );
}
